import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { AdminSession, CustomerSession, InternalUserRole } from 'libs/tm-common/src/lib/models/user.model';
import { AppState } from 'libs/tm-common/src/lib/store/reducers';

import {AuthService} from './auth.lib.service';
import { EnrichAuthStateAdminAction, LogoutAction} from '../store/auth.actions';
import { storeRedirectParams } from 'libs/tm-common/src/lib/store/redirect-state/redirect.actions';

/**
 * Creates a guard function that checks if the user has any of the required roles
 * @param requiredRoles - Array of roles that the user must have (user needs ANY of these roles)
 * @returns Guard function
 */
const createRoleBasedGuard = (requiredRoles: InternalUserRole[]) => {
	return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
		const authService = inject(AuthService);
		const store = inject(Store<AppState>);

		return authService.getMe().pipe(
			map((res: CustomerSession | AdminSession | { isLoggedIn: false }) => {
				const hasRequiredRole = res?.isLoggedIn === true &&
					res?.tm_admin === "1" &&
					requiredRoles.some(role => res?.roles?.includes(role));

				if (hasRequiredRole) {
					//just refresh the redux store entries (not an actual login)
					store.dispatch(new EnrichAuthStateAdminAction(res));
					return true;
				}

				//make sure that keycloak session is deleted and clear the redux store
				store.dispatch(new LogoutAction());
				store.dispatch(storeRedirectParams({ path: state.url, queryParams: state.root.queryParams }));
				return false;
			})
		);
	};
};

export const adminAreaGuard = createRoleBasedGuard([InternalUserRole.ADMIN]);

export const salesManagerAreaGuard = createRoleBasedGuard([InternalUserRole.SALES_MANAGER, InternalUserRole.ADMIN]);

@Injectable({
	providedIn: 'root'
})
export class AdminAuthGuard implements CanActivate {
	router = inject(Router);
	private authService = inject(AuthService);
	private store = inject<Store<AppState>>(Store);

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {

		return this.authService.getMe().pipe(
			take(1),
			map((res: CustomerSession | AdminSession | { isLoggedIn: false }) => {
				if (res?.isLoggedIn === true && res?.tm_admin === "1"){
					//just refresh the redux store entries (not an actual login)
					this.store.dispatch(new EnrichAuthStateAdminAction(res));
					return true;
				}
				//make sure that keycloak session is deleted and clear the redux store
				this.store.dispatch(new LogoutAction());
				this.store.dispatch(storeRedirectParams({ path: state.url, queryParams: state.root.queryParams }));
				this.router.navigate(['signin']);
				return false;
			})
		);
	}
}
